(function () {
  'use strict';

  const ROOT = () => window.Shopify?.routes?.root || '/';

  function createBundleId() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  }

  function replaceTokens(template, values) {
    return Object.entries(values).reduce(
      (text, [key, value]) => text.replaceAll(`[[${key}]]`, String(value)),
      template || ''
    );
  }

  function money(cents) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(Math.max(0, Number(cents) || 0) / 100);
  }

  function rupees(cents) {
    return `Rs. ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.max(0, Number(cents) || 0) / 100)}`;
  }

  async function request(path, body) {
    const execute = () => fetch(`${ROOT()}${path}`, {
      method: body ? 'POST' : 'GET',
      headers: body ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
      body: body ? JSON.stringify(body) : undefined
    });
    const withRetry = window.cartAddFetchWithRetry ? () => window.cartAddFetchWithRetry(execute) : execute;
    const response = window.cartMutationQueue && body
      ? await window.cartMutationQueue(withRetry)
      : await withRetry();
    if (!response.ok) throw new Error(`cart_${response.status}`);
    return response.json();
  }

  class RetentionCampaign {
    constructor(root) {
      this.root = root;
      this.config = window.DTRetentionGiftSync?.parseConfig();
      this.cards = [...root.querySelectorAll('[data-retention-product]')];
      this.button = root.querySelector('[data-add-campaign]');
      this.error = root.querySelector('[data-campaign-error]');
      this.preview = root.querySelector('[data-retention-preview]');
      this.cart = null;
      this.isAdding = false;
      this.previewProduct = null;
      this.previewVariant = null;
      this.previewCard = null;
      this.previewImages = [];
      this.previewImageIndex = 0;
      this.previewState = 'closed';
      this.previewCloseTimer = null;
      this.summaryCloseTimer = null;
      this.previewPointer = null;
      this.suppressPreviewClickUntil = 0;
      this.temporarySelections = new Map();
      this.summaryExpanded = false;

      if (!this.config || !this.button) return;
      this.bind();
      this.refreshCart();
    }

    bind() {
      this.cards.forEach((card) => {
        card.querySelector('[data-product-select]')?.addEventListener('change', () => this.render());
        card.querySelector('[data-retention-store-card]')?.addEventListener('change', () => {
          requestAnimationFrame(() => this.syncVariant(card));
        });
        card.querySelector('[data-retention-card-add]')?.addEventListener('click', (event) => {
          this.addCard(card, event.currentTarget);
        });
      });
      document.addEventListener('pointerdown', (event) => this.capturePreviewPointerDown(event), true);
      document.addEventListener('pointermove', (event) => this.capturePreviewPointerMove(event), { capture: true, passive: true });
      document.addEventListener('pointercancel', (event) => this.cancelPreviewPointer(event), true);
      document.addEventListener('pointerup', (event) => this.capturePreviewTap(event), true);
      window.addEventListener('click', (event) => this.capturePreviewTap(event), true);
      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.preview?.hidden) this.closePreview();
      });
      this.preview?.querySelectorAll('[data-preview-close]').forEach((button) => {
        button.addEventListener('click', () => this.closePreview());
      });
      this.preview?.addEventListener('pointerup', (event) => {
        event.stopPropagation();
      });
      this.preview?.addEventListener('click', (event) => {
        event.stopPropagation();
      });
      this.preview?.querySelector('[data-preview-gallery-scroll]')?.addEventListener('scroll', () => this.updatePreviewImageFromScroll(), { passive: true });
      this.preview?.querySelector('[data-preview-color-options]')?.addEventListener('click', (event) => {
        const button = event.target.closest('[data-preview-color]');
        if (button) this.selectPreviewColor(button.dataset.previewColor);
      });
      this.preview?.querySelector('[data-preview-add]')?.addEventListener('click', (event) => this.addPreviewItem(event.currentTarget));
      this.button.addEventListener('click', () => this.addAll());
      this.root.querySelector('[data-retention-summary-toggle]')?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggleSummary();
      });
      this.root.querySelector('[data-retention-summary-lines]')?.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-temp-remove]');
        if (!remove) return;
        this.temporarySelections.delete(remove.dataset.tempRemove);
        this.render();
      });
      document.addEventListener('cart:updated', (event) => {
        if (event.detail?.items) {
          this.cart = event.detail;
          this.render();
        }
      });
    }

    capturePreviewPointerDown(event) {
      if (event.pointerType === 'mouse') return;
      const card = event.target.closest('[data-retention-product]');
      if (card && this.root.contains(card)) this.suppressPreviewClickUntil = 0;
      this.previewPointer = card && this.root.contains(card)
        ? {
            pointerId: event.pointerId,
            card,
            startX: event.clientX,
            startY: event.clientY,
            moved: false
          }
        : null;
    }

    capturePreviewPointerMove(event) {
      const pointer = this.previewPointer;
      if (!pointer || pointer.pointerId !== event.pointerId || pointer.moved) return;
      const distanceX = event.clientX - pointer.startX;
      const distanceY = event.clientY - pointer.startY;
      if ((distanceX * distanceX) + (distanceY * distanceY) > 100) pointer.moved = true;
    }

    cancelPreviewPointer(event) {
      if (this.previewPointer?.pointerId !== event.pointerId) return;
      this.previewPointer = null;
      this.suppressPreviewClickUntil = Date.now() + 500;
    }

    capturePreviewTap(event) {
      if (this.preview?.contains(event.target)) {
        if (event.target.closest('a[href]')) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
        return;
      }
      if (event.type === 'pointerup') {
        if (event.pointerType === 'mouse') return;
        const pointer = this.previewPointer;
        this.previewPointer = null;
        if (!pointer || pointer.pointerId !== event.pointerId) return;

        const card = event.target.closest('[data-retention-product]');
        if (pointer.moved || card !== pointer.card || !this.root.contains(card)) {
          this.suppressPreviewClickUntil = Date.now() + 500;
        }
        return;
      }

      const card = event.target.closest('[data-retention-product]');
      if (Date.now() < this.suppressPreviewClickUntil) {
        if (card && this.root.contains(card)) {
          event.preventDefault();
          event.stopImmediatePropagation();
        }
        return;
      }
      if (!card || !this.root.contains(card)) return;
      this.openPreview(event, card);
    }

    itemFromCard(card) {
      const variantControl = card.querySelector('[data-product-variant]');
      const selectedOption = variantControl?.selectedOptions?.[0] || variantControl;
      const variantId = Number(selectedOption?.value || 0);
      const price = Number(selectedOption?.dataset.price || 0);
      if (!variantId || selectedOption?.dataset.available === 'false') return null;
      let variant = null;
      try {
        const variants = JSON.parse(card.querySelector('[data-retention-variants]')?.textContent || '[]');
        variant = variants.find((entry) => Number(entry.id) === variantId) || null;
      } catch (_) {}
      return {
        id: variantId,
        quantity: 1,
        price,
        compareAtPrice: Number(variant?.compare_at_price || 0),
        title: card.dataset.previewTitle || '',
        variantTitle: variant?.public_title || variant?.title || '',
        image: variant?.featured_image?.src || card.dataset.previewImage || ''
      };
    }

    openPreview(event, card) {
      const interactive = event.target.closest('button, input, label, select, quantity-input, .swatch-component, .card-gallery-controls');
      if (interactive) return;

      const productLink = event.target.closest('a[href*="/products/"]');
      if (!productLink && !event.target.closest('.card-wrapper')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.showPreview(card);
    }

    showPreview(card) {
      if (!this.preview) return;
      const productId = card.dataset.productId || '';
      if (!productId) return;
      if (this.previewState === 'open' && this.preview.dataset.productId === productId) return;

      const product = this.previewProductFromCard(card);
      if (!product) return;

      window.clearTimeout(this.previewCloseTimer);
      this.previewState = 'open';
      this.preview.dataset.productId = productId;
      this.preview.hidden = false;
      this.preview.classList.remove('is-closing');
      requestAnimationFrame(() => this.preview?.classList.add('is-open'));
      document.body.classList.add('overflow-hidden');
      const currentVariantId = this.itemFromCard(card)?.id;
      this.previewCard = card;
      this.previewProduct = product;
      this.previewVariant = product.variants.find((variant) => Number(variant.id) === Number(currentVariantId))
        || product.variants.find((variant) => variant.available)
        || product.variants[0];
      this.previewImages = product.images;
      this.previewImageIndex = this.initialPreviewImageIndex();
      this.renderPreview();
      this.preview.querySelector('[data-preview-content]').hidden = false;
      this.preview.querySelector('[data-preview-footer]').hidden = false;
      this.preview.querySelector('[data-preview-close]')?.focus();
    }

    previewProductFromCard(card) {
      try {
        const variants = JSON.parse(card.querySelector('[data-retention-variants]')?.textContent || '[]');
        const preview = JSON.parse(card.querySelector('[data-retention-preview-data]')?.textContent || '{}');
        const gallery = JSON.parse(card.querySelector('[data-card-gallery-images]')?.textContent || '[]');
        const cardImage = card.querySelector('[data-card-gallery-current-image] img, .card__media img');
        const images = gallery.map((image) => image?.src).filter(Boolean);
        if (cardImage?.currentSrc && images.length) images[0] = cardImage.currentSrc;
        if (!images.length && card.dataset.previewImage) images.push(card.dataset.previewImage);
        if (!variants.length) return null;
        return {
          title: card.dataset.previewTitle || '',
          description: preview.description || '',
          options: preview.options || [],
          colorSwatches: preview.colorSwatches || {},
          variants,
          images
        };
      } catch (_) {
        return null;
      }
    }

    closePreview() {
      if (!this.preview || this.previewState === 'closed' || this.previewState === 'closing') return;
      this.previewState = 'closing';
      this.preview.classList.remove('is-open');
      this.preview.classList.add('is-closing');
      const finish = () => {
        if (this.previewState !== 'closing') return;
        this.preview.hidden = true;
        this.preview.classList.remove('is-closing');
        this.previewState = 'closed';
        document.body.classList.remove('overflow-hidden');
      };
      if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        finish();
      } else {
        this.previewCloseTimer = window.setTimeout(finish, 360);
      }
    }

    initialPreviewImageIndex() {
      const variantImage = this.previewVariant?.featured_image?.src || this.previewVariant?.featured_image;
      if (!variantImage) return 0;
      const index = this.previewImages.findIndex((image) => image.split('?')[0] === String(variantImage).split('?')[0]);
      return index >= 0 ? index : 0;
    }

    renderPreview() {
      if (!this.previewProduct || !this.previewVariant || !this.preview) return;
      this.preview.querySelector('[data-preview-title]').textContent = this.previewProduct.title || '';
      const description = this.preview.querySelector('[data-preview-description]');
      const parsed = new DOMParser().parseFromString(this.previewProduct.description || '', 'text/html');
      parsed.body.querySelectorAll('img, video, iframe, script, style, form').forEach((element) => element.remove());
      parsed.body.querySelectorAll('a').forEach((anchor) => {
        const replacement = document.createElement('span');
        replacement.innerHTML = anchor.innerHTML;
        anchor.replaceWith(replacement);
      });
      description.replaceChildren(...[...parsed.body.childNodes].map((node) => document.importNode(node, true)));
      this.renderPreviewVariant();
      this.renderPreviewColors();
      this.renderPreviewImage();
    }

    renderPreviewVariant() {
      const price = this.preview.querySelector('[data-preview-price]');
      const addButton = this.preview.querySelector('[data-preview-add]');
      if (!price || !addButton || !this.previewVariant) return;

      price.textContent = money(this.previewVariant.price);
      if (Number(this.previewVariant.compare_at_price) > Number(this.previewVariant.price)) {
        const compare = document.createElement('s');
        compare.textContent = money(this.previewVariant.compare_at_price);
        price.appendChild(compare);
      }
      addButton.disabled = this.isAdding || !this.previewVariant.available;
      addButton.textContent = this.previewVariant.available ? 'Add' : 'Sold out';
    }

    colorOption() {
      return (this.previewProduct?.options || []).find((option) => {
        const name = typeof option === 'string' ? option : option.name;
        return /colou?r/i.test(name || '');
      });
    }

    previewVariantOption(variant, index) {
      return variant?.options?.[index] ?? variant?.[`option${index + 1}`];
    }

    renderPreviewColors() {
      const wrapper = this.preview.querySelector('[data-preview-colors]');
      const options = this.preview.querySelector('[data-preview-color-options]');
      const colorOption = this.colorOption();
      if (!wrapper || !options || !colorOption) {
        if (wrapper) wrapper.hidden = true;
        return;
      }

      const optionIndex = Math.max(0, Number(colorOption.position || (this.previewProduct.options.indexOf(colorOption) + 1)) - 1);
      const values = colorOption.values || [...new Set(this.previewProduct.variants.map((variant) => this.previewVariantOption(variant, optionIndex)))];
      options.replaceChildren(...values.filter(Boolean).map((value) => {
        const color = typeof value === 'object' ? value.value || value.name : value;
        const button = document.createElement('button');
        const matchingVariants = this.previewProduct.variants.filter((variant) => this.previewVariantOption(variant, optionIndex) === color);
        button.type = 'button';
        button.className = 'dt-retention-preview__color';
        button.dataset.previewColor = color;
        button.disabled = !matchingVariants.some((variant) => variant.available);
        button.classList.toggle('is-selected', this.previewVariantOption(this.previewVariant, optionIndex) === color);
        const swatchValue = this.previewProduct.colorSwatches?.[color];
        if (swatchValue) {
          const swatch = document.createElement('span');
          swatch.className = 'dt-retention-preview__color-swatch';
          swatch.style.background = swatchValue;
          button.append(swatch);
        }
        button.append(document.createTextNode(color));
        return button;
      }));
      wrapper.hidden = false;
    }

    selectPreviewColor(color) {
      const colorOption = this.colorOption();
      if (!colorOption) return;
      const optionIndex = Math.max(0, Number(colorOption.position || (this.previewProduct.options.indexOf(colorOption) + 1)) - 1);
      const variant = this.previewProduct.variants.find((item) => item.available && this.previewVariantOption(item, optionIndex) === color)
        || this.previewProduct.variants.find((item) => this.previewVariantOption(item, optionIndex) === color);
      if (!variant) return;
      this.previewVariant = variant;
      this.previewImageIndex = this.initialPreviewImageIndex();
      this.renderPreviewVariant();
      this.renderPreviewColors();
      this.renderPreviewImage();
    }

    renderPreviewImage() {
      const gallery = this.preview.querySelector('[data-preview-gallery-scroll]');
      const track = this.preview.querySelector('[data-preview-image-track]');
      const counter = this.preview.querySelector('[data-preview-counter]');
      if (!gallery || !track || !counter) return;

      const imageKey = this.previewImages.join('|');
      if (track.dataset.previewImages !== imageKey) {
        track.dataset.previewImages = imageKey;
        track.replaceChildren(...this.previewImages.map((src, index) => {
          const image = document.createElement('img');
          image.src = src;
          image.alt = this.previewProduct.title || '';
          image.width = 720;
          image.height = 720;
          image.loading = index === this.previewImageIndex ? 'eager' : 'lazy';
          return image;
        }));
      }
      this.updatePreviewCounter();
      window.requestAnimationFrame(() => {
        gallery.scrollTo({ left: gallery.clientWidth * this.previewImageIndex, behavior: 'auto' });
      });
    }

    updatePreviewImageFromScroll() {
      const gallery = this.preview?.querySelector('[data-preview-gallery-scroll]');
      if (!gallery || this.previewImages.length <= 1 || !gallery.clientWidth) return;
      const imageIndex = Math.round(gallery.scrollLeft / gallery.clientWidth);
      if (imageIndex === this.previewImageIndex) return;
      this.previewImageIndex = Math.max(0, Math.min(this.previewImages.length - 1, imageIndex));
      this.updatePreviewCounter();
    }

    updatePreviewCounter() {
      const counter = this.preview?.querySelector('[data-preview-counter]');
      if (counter) counter.textContent = this.previewImages.length > 1 ? `${this.previewImageIndex + 1} / ${this.previewImages.length}` : '';
    }

    addPreviewItem() {
      if (!this.previewVariant?.available) return;
      const card = this.previewCard;
      if (!card) return;
      this.temporarySelections.set(card.dataset.productId, {
        id: Number(this.previewVariant.id),
        quantity: 1,
        price: Number(this.previewVariant.price || 0),
        image: this.previewImages[this.previewImageIndex] || this.previewVariant.featured_image?.src || ''
      });
      this.showError('');
      this.render();
      this.closePreview();
    }

    syncVariant(card) {
      const campaignVariant = card.querySelector('[data-product-variant]');
      const storeVariant = card.querySelector('.product-variant-id');
      const variantsNode = card.querySelector('[data-retention-variants]');
      if (!campaignVariant || !storeVariant?.value || !variantsNode) return;

      try {
        const variants = JSON.parse(variantsNode.textContent || '[]');
        const variant = variants.find((item) => Number(item.id) === Number(storeVariant.value));
        if (!variant) return;
        campaignVariant.value = variant.id;
        campaignVariant.dataset.price = variant.price;
        campaignVariant.dataset.available = String(variant.available);
        const checkbox = card.querySelector('[data-product-select]');
        if (!variant.available && checkbox) checkbox.checked = false;
        this.render();
      } catch (error) {
        this.showError(this.root.dataset.copyError);
      }
    }

    selectedItems() {
      return [...this.temporarySelections.entries()].map(([productId, item]) => {
        const card = this.cards.find((candidate) => candidate.dataset.productId === productId);
        return {
          id: item.id,
          quantity: item.quantity,
          price: item.price,
          title: card?.dataset.previewTitle || 'Selected product',
          image: item.image || card?.dataset.previewImage || ''
        };
      });
    }

    projectedState(selected = this.selectedItems()) {
      const selectedValue = selected.reduce((sum, item) => sum + item.price * item.quantity, 0);
      const projected = selectedValue;
      const tier = window.DTRetentionGiftSync.unlockedTier(projected, this.config);
      return { selected, existing: 0, projected, tier };
    }

    render() {
      const state = this.projectedState();
      this.cards.forEach((card) => {
        const item = this.itemFromCard(card);
        const selected = item && this.temporarySelections.get(card.dataset.productId);
        const isSelected = Boolean(selected && Number(selected.id) === Number(item.id));
        const checkbox = card.querySelector('[data-product-select]');
        if (checkbox) checkbox.checked = Boolean(selected);
        card.classList.toggle('is-selected', isSelected);
        const addButton = card.querySelector('[data-retention-card-add]');
        if (addButton) {
          addButton.disabled = this.isAdding || !item;
          addButton.querySelector('span').textContent = isSelected ? 'Added' : (item ? 'Add' : 'Sold out');
        }
      });

      const existingNode = this.root.querySelector('[data-existing-cart-value]');
      if (existingNode) {
        existingNode.hidden = true;
      }

      const nextTier = this.config.tiers.find((tier) => state.projected < tier.threshold);
      const summary = this.root.querySelector('[data-retention-summary]');
      const progressRatio = state.tier ? 1 : Math.min(1, state.projected / (nextTier?.threshold || 149900));
      summary?.style.setProperty('--dt-retention-progress', String(progressRatio));
      summary?.classList.toggle('has-selection', state.selected.length > 0);
      summary?.classList.toggle('is-unlocked', Boolean(state.tier));
      const progress = this.root.querySelector('[data-progress-message]');
      if (progress) {
        progress.textContent = nextTier
          ? replaceTokens(this.root.dataset.copyAddMore, {
              amount: money((nextTier?.threshold || 149900) - state.projected),
              gift: `₹${nextTier?.worth || 699}`
            })
          : replaceTokens(this.root.dataset.copyUnlocked, { gift: `₹${state.tier?.worth || 1499}` });
      }

      this.root.querySelectorAll('[data-tier-card]').forEach((card) => {
        const threshold = Number(card.dataset.tierCard || 0);
        card.classList.toggle('is-unlocked', state.projected >= threshold);
        card.classList.toggle('is-current', Boolean(state.tier && state.tier.threshold === threshold));
      });

      this.root.querySelectorAll('[data-summary-tier]').forEach((tier) => {
        tier.classList.toggle('is-unlocked', state.projected >= Number(tier.dataset.summaryTier || 0));
      });
      this.root.querySelectorAll('[data-summary-divider]').forEach((divider) => {
        divider.classList.toggle('is-unlocked', state.projected >= Number(divider.dataset.summaryDivider || 0));
      });

      this.button.disabled = this.isAdding || !state.tier;
      this.renderTemporaryLines(state);
    }

    toggleSummary() {
      this.summaryExpanded = !this.summaryExpanded;
      const summary = this.root.querySelector('[data-retention-summary]');
      const toggle = this.root.querySelector('[data-retention-summary-toggle]');
      const lines = this.root.querySelector('[data-retention-summary-lines]');
      window.clearTimeout(this.summaryCloseTimer);

      if (this.summaryExpanded) {
        if (lines) lines.hidden = false;
        window.requestAnimationFrame(() => summary?.classList.add('is-expanded'));
      } else {
        summary?.classList.remove('is-expanded');
        this.summaryCloseTimer = window.setTimeout(() => {
          if (lines && !this.summaryExpanded) lines.hidden = true;
        }, 320);
      }

      if (toggle) {
        toggle.setAttribute('aria-expanded', String(this.summaryExpanded));
        toggle.setAttribute('aria-label', this.summaryExpanded ? 'Hide selected products' : 'Show selected products');
      }
      toggle?.classList.toggle('is-expanded', this.summaryExpanded);
    }

    renderTemporaryLines(state = this.projectedState()) {
      const lines = this.root.querySelector('[data-retention-summary-lines]');
      if (!lines) return;
      const lineNodes = [...this.temporarySelections.entries()].map(([key, item]) => {
        const card = this.cards.find((candidate) => candidate.dataset.productId === key);
        const line = document.createElement('div');
        line.className = 'dt-retention-summary__line dt-retention-summary__line--product';
        const image = document.createElement('img');
        image.className = 'dt-retention-summary__line-image';
        image.src = item.image || card?.dataset.previewImage || '';
        image.alt = card?.dataset.previewTitle || 'Selected product';
        image.width = 32;
        image.height = 32;
        image.loading = 'lazy';
        const copy = document.createElement('div');
        copy.className = 'dt-retention-summary__line-copy';
        const title = document.createElement('span');
        title.textContent = card?.dataset.previewTitle || 'Selected product';
        const price = document.createElement('span');
        price.className = 'dt-retention-summary__line-price';
        price.textContent = rupees(item.price * item.quantity);
        copy.append(title);
        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'dt-retention-summary__remove';
        remove.dataset.tempRemove = key;
        remove.setAttribute('aria-label', `Remove ${title.textContent}`);
        const removeIcon = document.createElement('img');
        removeIcon.src = this.root.dataset.removeIcon || '';
        removeIcon.alt = '';
        removeIcon.width = 16;
        removeIcon.height = 16;
        remove.append(removeIcon);
        line.append(image, copy, price, remove);
        return line;
      });

      if (state.tier) {
        const giftLine = document.createElement('div');
        giftLine.className = 'dt-retention-summary__line dt-retention-summary__line--gift';
        const giftImage = document.createElement('img');
        giftImage.className = 'dt-retention-summary__line-image';
        giftImage.src = state.tier.image || this.root.dataset.giftFallback || '';
        giftImage.alt = state.tier.title || 'Free gift';
        giftImage.width = 32;
        giftImage.height = 32;
        giftImage.loading = 'lazy';
        const giftCopy = document.createElement('div');
        giftCopy.className = 'dt-retention-summary__line-copy';
        const giftTitle = document.createElement('span');
        giftTitle.textContent = state.tier.title || `Free Gift Worth ₹${state.tier.worth}`;
        giftCopy.append(giftTitle);
        const giftBadge = document.createElement('span');
        giftBadge.className = 'dt-retention-summary__gift-badge';
        giftBadge.textContent = 'FREE';
        giftLine.append(giftImage, giftCopy, giftBadge);
        lineNodes.push(giftLine);
      }

      const content = document.createElement('div');
      content.className = 'dt-retention-summary__lines-content';
      content.append(...lineNodes);
      lines.replaceChildren(content);
      if (this.summaryExpanded) lines.hidden = false;
    }

    async refreshCart() {
      try {
        this.cart = await request('cart.js');
        this.render();
      } catch (error) {
        this.showError(this.root.dataset.copyError);
      }
    }

    showError(message) {
      if (!this.error) return;
      this.error.hidden = !message;
      this.error.textContent = message || '';
    }

    async refreshCartUI(opener = this.button) {
      if (window.CartController?.finishAddToCartRefresh) {
        await window.CartController.finishAddToCartRefresh({
          refreshRecommendations: true,
          refreshItems: true,
          refreshCartPage: false
        });
        window.CartController.openDrawer?.({ skipRefresh: true });
        return;
      }
      this.cart = await request('cart.js');
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: this.cart }));
      document.querySelector('cart-drawer')?.open?.(opener);
    }

    async addAll() {
      const selected = this.selectedItems();
      if (!selected.length || !this.projectedState(selected).tier) return;
      await this.addItems(selected, this.button, true);
    }

    async addCard(card, button) {
      const item = this.itemFromCard(card);
      if (!item) return;
      const key = card.dataset.productId;
      const selected = this.temporarySelections.get(key);
      if (selected && Number(selected.id) === Number(item.id)) {
        this.temporarySelections.delete(key);
      } else {
        this.temporarySelections.set(key, item);
      }
      this.showError('');
      this.render();
    }

    async addItems(itemsToAdd, trigger, clearSelections) {
      if (this.isAdding || !itemsToAdd.length) return false;
      this.isAdding = true;
      this.showError('');
      const previousContent = trigger?.innerHTML;
      if (trigger) {
        trigger.textContent = trigger === this.button ? this.root.dataset.copyAdding : this.root.dataset.copyCardAdding;
      }
      this.render();

      let previousCart = null;
      let bundleAdded = false;
      try {
        const state = this.projectedState(itemsToAdd);
        // A sold-out tier gift must not block the paid items: add them without the gift and say so.
        const giftTier = state.tier?.available && state.tier.variantId ? state.tier : null;
        const giftSoldOut = Boolean(state.tier && !giftTier);

        const bundleId = createBundleId();
        if (giftTier && window.CartController?.addOptimisticFreeGiftBundle) {
          previousCart = window.CartController.addOptimisticFreeGiftBundle({
            bundleId,
            title: `Buy for ₹${giftTier.threshold / 100} and get a freebie worth ₹${giftTier.worth}`,
            total: itemsToAdd.reduce((sum, item) => sum + item.price * item.quantity, 0),
            originalTotal: itemsToAdd.reduce((sum, item) => sum + item.price * item.quantity, 0) + giftTier.worth * 100,
            items: itemsToAdd,
            gift: {
              variantId: giftTier.variantId,
              title: giftTier.title || `Free gift worth ₹${giftTier.worth}`,
              image: giftTier.image,
              price: 0,
              originalPrice: giftTier.worth * 100
            }
          });
        }

        await request('cart/update.js', {
          updates: {},
          attributes: { [window.DTRetentionGiftSync.campaignAttribute]: window.DTRetentionGiftSync.campaignValue }
        });

        const items = itemsToAdd.map(({ id, quantity }) => ({
          id,
          quantity,
          properties: { _dt_retention_bundle: bundleId }
        }));
        if (giftTier) {
          items.push({
            id: giftTier.variantId,
            quantity: 1,
            properties: {
              _dt_retention_bundle: bundleId,
              _dt_retention_gift: String(giftTier.threshold)
            }
          });
        }
        if (items.length) await request('cart/add.js', { items });
        bundleAdded = true;

        await this.refreshCartUI(trigger);
        await window.DTRetentionGiftSync.reconcile();
        this.cart = await request('cart.js');
        if (clearSelections) {
          this.temporarySelections.clear();
          if (this.summaryExpanded) this.toggleSummary();
        }
        if (giftSoldOut) {
          this.showError(this.root.dataset.copyGiftSoldOut || 'Added to cart. The free gift is out of stock right now.');
        }
        return true;
      } catch (error) {
        if (!bundleAdded) {
          window.CartController?.rollbackOptimisticFreeGiftBundle?.(previousCart);
        } else {
          await this.refreshCartUI(trigger).catch(() => {});
        }
        this.showError(error.message === 'gift_unavailable' ? this.root.dataset.copyGiftUnavailable : this.root.dataset.copyError);
        return false;
      } finally {
        this.isAdding = false;
        if (trigger && previousContent != null) trigger.innerHTML = previousContent;
        this.render();
      }
    }
  }

  function init() {
    if (!window.DTRetentionGiftSync?.parseConfig) {
      window.setTimeout(init, 100);
      return;
    }
    document.querySelectorAll('[data-retention-campaign]').forEach((root) => {
      if (root.dataset.retentionInitialized === 'true') return;
      new RetentionCampaign(root);
      root.dataset.retentionInitialized = 'true';
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
